<div class="container mx-auto space-y-6 px-4 py-6">
  <header>
    <h1 hlmH3>Auszahlungen</h1>
    <p hlmMuted class="mt-1">
      {{ networkName() }} rechnet deine Einlösungen einmal im Monat ab und überweist sie auf dein
      Konto.
    </p>
  </header>

  @if (company() && !company()!.has_bank_account) {
    <div hlmAlert variant="warning">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>Bankverbindung hinterlegen</p>
      <p hlmAlertDescription>
        Ohne IBAN kann {{ networkName() }} dir deine Einlösungen nicht überweisen.
      </p>
      <a
        hlmBtn
        variant="outline"
        size="sm"
        class="mt-2"
        [routerLink]="['/settings']"
        [queryParams]="{ tab: 'bank-account' }">
        Jetzt hinterlegen
        <ng-icon name="lucideArrowRight" data-icon="inline-end" />
      </a>
    </div>
  }

  @if (error(); as message) {
    <div hlmAlert variant="destructive">
      <ng-icon name="lucideTriangleAlert" />
      <p hlmAlertTitle>{{ message }}</p>
    </div>
  }

  @if (loading()) {
    <div class="grid gap-4 sm:grid-cols-3">
      <hlm-skeleton class="h-28 rounded-xl" />
      <hlm-skeleton class="h-28 rounded-xl" />
      <hlm-skeleton class="h-28 rounded-xl" />
    </div>
    <hlm-skeleton class="h-64 w-full rounded-xl" />
  } @else if (!error()) {
    <div class="grid gap-4 sm:grid-cols-3">
      <app-kpi-card
        accent="default"
        label="Diesen Monat"
        [value]="money(runningMonth().payoutCents)"
        [subtext]="runningMonth().count + ' Einlösungen · Abrechnung nach Monatsende'">
        <ng-template #icon><ng-icon name="lucideCalendar" /></ng-template>
      </app-kpi-card>
      <app-kpi-card
        accent="default"
        label="Noch nicht überwiesen"
        [value]="money(outstandingCents())"
        subtext="Kommt mit der nächsten Abrechnung">
        <ng-template #icon><ng-icon name="lucideWallet" /></ng-template>
      </app-kpi-card>
      <app-kpi-card
        accent="default"
        label="Zuletzt überwiesen"
        [value]="lastPaid() ? money(lastPaid()!.payout_cents) : '–'"
        [subtext]="lastPaidSubtext() ?? 'Noch keine Überweisung'">
        <ng-template #icon><ng-icon name="lucideBanknote" /></ng-template>
      </app-kpi-card>
    </div>

    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Monatsübersicht</h2>
        <p hlmCardDescription>
          Was du je Monat eingelöst hast und was dir {{ networkName() }} dafür überweist.
        </p>
      </div>
      <div hlmCardContent>
        <div hlmTableContainer>
          <table hlmTable>
            <thead hlmTHead>
              <tr hlmTr>
                <th hlmTh>Monat</th>
                <th hlmTh class="text-right">Einlösungen</th>
                <th hlmTh class="text-right">Eingelöst</th>
                <th hlmTh class="text-right">Auszahlung</th>
                <th hlmTh>Status</th>
              </tr>
            </thead>
            <tbody hlmTBody>
              @for (month of months(); track month.period) {
                <tr hlmTr>
                  <td hlmTd>{{ month.label }}</td>
                  <td hlmTd class="text-right tabular-nums">{{ month.count }}</td>
                  <td hlmTd class="text-right tabular-nums">{{ money(month.redeemedCents) }}</td>
                  <td hlmTd class="text-right tabular-nums">{{ money(month.payoutCents) }}</td>
                  <td hlmTd>
                    <span hlmBadge [variant]="statusVariant(month.status)">
                      {{ statusLabel(month.status) }}
                    </span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Abrechnungen</h2>
        <p hlmCardDescription>
          Die monatlichen Sammelüberweisungen von {{ networkName() }}. Eine Einlösung ohne
          hinterlegte IBAN wird mit der nächsten Abrechnung überwiesen.
        </p>
      </div>
      <div hlmCardContent>
        @if (visibleSettlements().length === 0) {
          <p hlmMuted class="text-sm">Noch keine Abrechnungen.</p>
        } @else {
          <div hlmTableContainer>
            <table hlmTable>
              <thead hlmTHead>
                <tr hlmTr>
                  <th hlmTh>Monat</th>
                  <th hlmTh class="text-right">Einlösungen</th>
                  <th hlmTh class="text-right">Betrag</th>
                  <th hlmTh>Konto</th>
                  <th hlmTh>Status</th>
                </tr>
              </thead>
              <tbody hlmTBody>
                @for (s of visibleSettlements(); track s.period) {
                  <tr hlmTr>
                    <td hlmTd>{{ s.period_label }}</td>
                    <td hlmTd class="text-right tabular-nums">{{ s.redemption_count }}</td>
                    <td hlmTd class="text-right tabular-nums">{{ money(s.payout_cents) }}</td>
                    <td hlmTd class="font-mono text-xs">{{ s.iban_masked || '–' }}</td>
                    <td hlmTd>
                      @if (s.status === 'paid') {
                        <span hlmBadge variant="solid">Überwiesen am {{ date(s.paid_at) }}</span>
                      } @else {
                        <span hlmBadge variant="outline">In Abrechnung</span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </section>
  }
</div>
